// biome-ignore lint/style/noRestrictedImports: central debug sink
import debug from 'debug';

interface SSRDebugContext {
  interfaceId?: string;
  pageId?: string;
  appId?: string;
  extraInfo?: Record<string, unknown>;
}

// four major product lines of UnifyApps
const debugNoCode = debug('no-code');
const debugData = debug('data');
const debugAutomations = debug('automations');
const debugAi = debug('ai');

const debugApp = debug('app');

// a feature that spans across

const debugSidenav = debugApp.extend('sidenav');

// automations product line
const debugAutomationsInputForm = debugAutomations.extend('input-form');
const debugAutomationsCopyPaste = debugAutomations.extend('copy-paste');

// ai product line
const debugAiCopilot = debugAi.extend('copilot');

// no-code product line
// CodeMirror expression-editor autocomplete trace.
// Enable with `localStorage.debug = 'no-code:cm-autocomplete'`.
const debugCmAutocomplete = debugNoCode.extend('cm-autocomplete');
const debugNoCodeSSR = debugNoCode.extend('ssr');
const debugNoCodeInteractions = debugNoCode.extend('interactions');
const debugNoCodeCopyPaste = debugNoCode.extend('copy-paste');
// component-overrides v1: enable with `localStorage.debug = 'no-code:slot-props:runtime'`
const debugNoCodeSlotProps = debugNoCode.extend('slot-props');
const debugComponentOverridesRuntime = debugNoCodeSlotProps.extend('runtime');

/**
 * Debug SSR prefetch failures
 * Logs when loading states are true (indicating SSR prefetch failed)
 * Includes context information and identifies which expectations failed
 *
 * Enable with: localStorage.debug = 'no-code:ssr'
 */
function debugSSR(
  hookName: string,
  loadingStates: Record<string, boolean>,
  context?: SSRDebugContext,
): void {
  if (!debugNoCodeSSR.enabled) {
    return;
  }

  // only log if at least one loading state is true (SSR prefetch failure)
  const hasFailures = Object.values(loadingStates).some((isLoading) => isLoading);
  if (!hasFailures) {
    return;
  }

  // identify which loading states failed (are true)
  const failedStates = Object.entries(loadingStates)
    .filter(([, isLoading]) => isLoading)
    .map(([name]) => name);

  const contextInfo: string[] = [];
  if (context?.interfaceId) {
    contextInfo.push(`interfaceId:${context.interfaceId}`);
  }
  if (context?.pageId) {
    contextInfo.push(`pageId:${context.pageId}`);
  }
  if (context?.appId) {
    contextInfo.push(`appId:${context.appId}`);
  }

  const contextStr = contextInfo.length > 0 ? ` [${contextInfo.join(' ')}]` : '';

  // format all loading states for reference
  const statesStr = Object.entries(loadingStates)
    .map(([name, isLoading]) => `${name}:${isLoading}`)
    .join(' ');

  debugNoCodeSSR(
    `${hookName}${contextStr} - Failed expectations: ${failedStates.join(', ')} | All states: ${statesStr}`,
  );

  if (context?.extraInfo) {
    debugNoCodeSSR(`extraInfo:${JSON.stringify(context.extraInfo)}`);
  }
}

function debugNoCodeLog(label: string, ...args: unknown[]) {
  if (!debugNoCode.enabled) return;
  debugNoCode(label, ...args);
}

export {
  debugAi,
  debugAiCopilot,
  debugAutomations,
  debugAutomationsCopyPaste,
  debugAutomationsInputForm,
  debugCmAutocomplete,
  debugComponentOverridesRuntime,
  debugData,
  debugNoCode,
  debugNoCodeCopyPaste,
  debugNoCodeInteractions,
  debugNoCodeLog,
  debugNoCodeSlotProps,
  debugSidenav,
  debugSSR,
};
